// FILE: src/components/AdminAddRestaurant.js
// New component for the admin to search for and add a new restaurant.

import React, { useState } from 'react';
import { X, PlusCircle, Search, CheckCircle } from 'lucide-react';

export default function AdminAddRestaurant({ users, onClose, onAdd }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [ownerEmail, setOwnerEmail] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        e.preventDefault();
        // In a real application, you would call an API here to search for restaurants.
        // For this example, we'll use a mock search result.
        const mockResults = [
            { name: `${searchTerm} - Example A`, address: '123 Fake St, St Helens', cuisine: 'Mock Italian' },
            { name: `${searchTerm} - Example B`, address: '456 Another Rd, St Helens', cuisine: 'Mock British' },
        ];
        setSearchResults(mockResults);
    };

    const handleSelectRestaurant = (restaurant) => {
        setSelectedRestaurant(restaurant);
        setSearchResults([]);
    };

    const handleAddRestaurant = async (e) => {
        e.preventDefault();
        setIsAdding(true);
        setError('');
        try {
            await onAdd({ ...selectedRestaurant, ownerEmail });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to add restaurant.');
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full relative animate-fade-in-up">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><X size={28} /></button>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Add New Restaurant</h2>

                {!selectedRestaurant ? (
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search for a restaurant name..."
                            className="w-full p-2 border rounded-md"
                        />
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
                            <Search size={18} /> Search
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleAddRestaurant} className="space-y-4">
                        <div>
                            <h3 className="text-xl font-bold">{selectedRestaurant.name}</h3>
                            <p className="text-gray-600">{selectedRestaurant.address}</p>
                            <p className="text-sm text-gray-500">Cuisine: {selectedRestaurant.cuisine}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Assign to Owner's Email</label>
                            <input
                                type="email"
                                value={ownerEmail}
                                onChange={(e) => setOwnerEmail(e.target.value)}
                                placeholder="owner@example.com"
                                className="w-full p-2 border rounded-md"
                                required
                            />
                        </div>
                        {error && <p className="text-red-500 text-sm">{error}</p>}
                        <div className="flex justify-end pt-4">
                            <button type="submit" disabled={isAdding} className="bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 disabled:bg-green-300 flex items-center gap-2">
                                <PlusCircle size={18} /> {isAdding ? 'Adding...' : 'Add Restaurant'}
                            </button>
                        </div>
                    </form>
                )}

                {searchResults.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                        <h3 className="font-semibold">Search Results</h3>
                        <ul className="space-y-2 mt-2">
                            {searchResults.map((result, index) => (
                                <li key={index} className="p-2 border rounded-md flex justify-between items-center">
                                    <div>
                                        <p className="font-semibold">{result.name}</p>
                                        <p className="text-sm text-gray-500">{result.address}</p>
                                    </div>
                                    <button onClick={() => handleSelectRestaurant(result)} className="bg-blue-500 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1">
                                        <CheckCircle size={16} /> Select
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
